// item-media.ts — orchestrator for a [data-ft-item-media] mount.
//
// Parses the media shape embedded up front in data-ft-media (no fetch), builds the Pictures and
// Videos galleries once, adopts the PHP-rendered Upload pane, and wires a header that pivots
// between the three panes purely client-side (toggle visibility — the catalog-list "build once,
// toggle" rule). Management actions live in the card renderers; the uploader appends new pictures
// live. Vanilla DOM + fetch, no jQuery.

import type { ItemMediaConfig, ItemMediaPayload, ItemPicture, ItemVideo } from './types';
import { renderPictureCard } from './render-picture';
import { renderVideoCard } from './render-video';
import { mountUploader } from './uploader';

type PaneKey = 'pictures' | 'videos' | 'upload';

function parseConfig(root: HTMLElement): ItemMediaConfig {
    const attr = (name: string): string => root.getAttribute(name) || '';
    return {
        mediaType: attr('data-ft-media-type') || attr('data-ft-attached-item-type') || 'item',
        attachedItemType: attr('data-ft-attached-item-type') || 'item',
        uploadEndpoint: attr('data-ft-upload-endpoint'),
        videoUploadEndpoint: attr('data-ft-video-upload-endpoint'),
        picPrimaryEndpoint: attr('data-ft-pic-primary-endpoint'),
        picDeleteEndpoint: attr('data-ft-pic-delete-endpoint'),
        picHardDeleteEndpoint: attr('data-ft-pic-harddelete-endpoint'),
        picEditBase: attr('data-ft-pic-edit-base'),
        videoPrimaryEndpoint: attr('data-ft-video-primary-endpoint'),
        videoDeleteEndpoint: attr('data-ft-video-delete-endpoint'),
        icons: {
            tabPictures: attr('data-ft-icon-tab-pictures'),
            tabVideos: attr('data-ft-icon-tab-videos'),
            tabUpload: attr('data-ft-icon-tab-upload'),
            primary: attr('data-ft-icon-primary'),
            edit: attr('data-ft-icon-edit'),
            delete: attr('data-ft-icon-delete'),
        },
        statusDivId: 'div_item_media_status',
    };
}

function parsePayload(root: HTMLElement): ItemMediaPayload {
    const empty: ItemMediaPayload = {
        item_id: '', item_name: '', pictures: [], videos: [],
        caps: { can_manage: false, can_hard_delete: false },
    };
    try {
        const parsed = JSON.parse(root.getAttribute('data-ft-media') || '');
        if (!parsed || typeof parsed !== 'object') return empty;
        return {
            item_id: String(parsed.item_id || ''),
            item_name: String(parsed.item_name || ''),
            pictures: Array.isArray(parsed.pictures) ? parsed.pictures : [],
            videos: Array.isArray(parsed.videos) ? parsed.videos : [],
            caps: {
                can_manage: !!parsed.caps?.can_manage,
                can_hard_delete: !!parsed.caps?.can_hard_delete,
            },
        };
    } catch {
        return empty;
    }
}

interface Tab {
    key: PaneKey;
    btn: HTMLButtonElement;
    pane: HTMLElement;
    badge: HTMLElement | null;
    optional: boolean; // pictures/videos hide when empty; upload is always present
}

function makePane(key: PaneKey): HTMLElement {
    const pane = document.createElement('div');
    pane.className = 'ft-media-pane';
    pane.setAttribute('data-ft-media-pane', key);
    pane.style.display = 'none';
    return pane;
}

function makeGrid(): HTMLElement {
    const grid = document.createElement('div');
    grid.className = 'row text-left';
    return grid;
}

// Hydrate any icon_ph() placeholders (tab + card-button icons) we inject. The footer's load-time
// ftMountIcons sweep only covers nodes present at page load, so anything built here — and especially
// cards appended live after an upload — must be re-hydrated explicitly or the buttons stay empty.
function hydrateIcons(scope: Element | Document): void {
    if (typeof window.ftMountIcons === 'function') {
        try { window.ftMountIcons(scope); } catch (e) { /* footer pass covers init */ }
    }
}

export function mountItemMedia(root: HTMLElement): void {
    if (root.getAttribute('data-ft-item-media-mounted') === '1') return;
    root.setAttribute('data-ft-item-media-mounted', '1');

    const config = parseConfig(root);
    const payload = parsePayload(root);

    const panes = root.querySelector<HTMLElement>('[data-ft-media-panes]');
    const tabsHost = root.querySelector<HTMLElement>('[data-ft-media-tabs]');
    if (!panes || !tabsHost) {
        console.error('[item-media] mount missing panes/tabs', root);
        return;
    }
    // Upload is a management action: only wire it when the viewer can manage AND the page shipped an
    // upload pane. A read-only mount (e.g. the public item_pictures.php gallery for a non-owner) ships
    // can_manage=false / no upload pane and gets Pictures + Videos only — no Upload tab, no uploader.
    const uploadPane = panes.querySelector<HTMLElement>('[data-ft-media-pane="upload"]') ?? null;
    const includeUpload = payload.caps.can_manage && !!uploadPane;
    if (uploadPane && !includeUpload) uploadPane.style.display = 'none';

    // ── Build Pictures + Videos panes (before the Upload pane when present, else appended) ──
    const picturesPane = makePane('pictures');
    const picturesGrid = makeGrid();
    picturesPane.appendChild(picturesGrid);

    const videosPane = makePane('videos');
    const videosGrid = makeGrid();
    videosPane.appendChild(videosGrid);

    if (uploadPane) {
        panes.insertBefore(picturesPane, uploadPane);
        panes.insertBefore(videosPane, uploadPane);
    } else {
        panes.appendChild(picturesPane);
        panes.appendChild(videosPane);
    }

    const tabs: Tab[] = [
        { key: 'pictures', btn: tabButton('Pictures', config.icons.tabPictures), pane: picturesPane, badge: null, optional: true },
        { key: 'videos', btn: tabButton('Videos', config.icons.tabVideos), pane: videosPane, badge: null, optional: true },
    ];
    if (includeUpload) {
        tabs.push({ key: 'upload', btn: tabButton('Upload', config.icons.tabUpload), pane: uploadPane!, badge: null, optional: false });
    }

    function tabButton(label: string, iconHtml: string): HTMLButtonElement {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'btn btn-outline-secondary ft-media-tab';
        if (iconHtml) {
            const ico = document.createElement('span');
            ico.className = 'ft-media-tab-icon';
            ico.innerHTML = iconHtml; // trusted icon_map() output
            btn.appendChild(ico);
        }
        btn.appendChild(document.createTextNode(' ' + label + ' '));
        const badge = document.createElement('span');
        badge.className = 'badge bg-secondary ft-media-tab-badge';
        btn.appendChild(badge);
        return btn;
    }

    // Attach badges + nav, then wire switching.
    const nav = document.createElement('div');
    nav.className = 'd-flex flex-wrap gap-2';
    for (const tab of tabs) {
        tab.badge = tab.btn.querySelector('.ft-media-tab-badge');
        nav.appendChild(tab.btn);
        tab.btn.addEventListener('click', () => activate(tab.key));
    }
    tabsHost.appendChild(nav);

    function setBadge(tab: Tab, n: number): void {
        if (!tab.badge) return;
        if (tab.key === 'upload') { tab.badge.style.display = 'none'; return; }
        tab.badge.textContent = String(n);
        tab.badge.style.display = n > 0 ? '' : 'none';
    }

    function setTabVisible(tab: Tab, visible: boolean): void {
        tab.btn.style.display = visible ? '' : 'none';
        syncNav();
    }

    // A tab strip of one is noise: an empty entity shows only Upload, which then reads as a second
    // upload button sitting above the dropzone's own submit. Hide the whole nav until there is an
    // actual choice to make (i.e. a gallery tab has appeared).
    function syncNav(): void {
        const visible = tabs.filter((t) => t.btn.style.display !== 'none').length;
        tabsHost!.style.display = visible > 1 ? '' : 'none';
    }

    function activate(key: PaneKey): void {
        for (const tab of tabs) {
            const on = tab.key === key;
            tab.pane.style.display = on ? '' : 'none';
            tab.btn.classList.toggle('active', on);
            tab.btn.classList.toggle('btn-secondary', on);
            tab.btn.classList.toggle('btn-outline-secondary', !on);
        }
    }

    // ── Render the cards ─────────────────────────────────────────────────────────────────
    let pictureCount = payload.pictures.length;
    let videoCount = payload.videos.length;
    const picturesTab = tabs[0]!;
    const videosTab = tabs[1]!;

    const decPictures = () => { pictureCount -= 1; setBadge(picturesTab, pictureCount); };
    const decVideos = () => { videoCount -= 1; setBadge(videosTab, videoCount); };

    for (const pic of payload.pictures) {
        picturesGrid.appendChild(renderPictureCard(pic, {
            config, itemId: payload.item_id,
            canManage: payload.caps.can_manage, canHardDelete: payload.caps.can_hard_delete,
            scope: picturesGrid, onRemove: decPictures,
        }));
    }
    for (const video of payload.videos) {
        videosGrid.appendChild(renderVideoCard(video, {
            config, itemId: payload.item_id,
            canManage: payload.caps.can_manage,
            scope: videosGrid, onRemove: decVideos,
        }));
    }

    setBadge(picturesTab, pictureCount);
    setBadge(videosTab, videoCount);
    setTabVisible(picturesTab, pictureCount > 0);
    setTabVisible(videosTab, videoCount > 0);

    const handleUploadedPictures = (pics: ItemPicture[]): void => {
        for (const pic of pics) {
            picturesGrid.appendChild(renderPictureCard(pic, {
                config, itemId: payload.item_id,
                canManage: payload.caps.can_manage, canHardDelete: payload.caps.can_hard_delete,
                scope: picturesGrid, onRemove: decPictures,
            }));
            pictureCount += 1;
        }
        hydrateIcons(picturesGrid);
        setBadge(picturesTab, pictureCount);
        setTabVisible(picturesTab, pictureCount > 0);
        activate('pictures');
    };

    const handleUploadedVideos = (vids: ItemVideo[]): void => {
        for (const video of vids) {
            videosGrid.appendChild(renderVideoCard(video, {
                config, itemId: payload.item_id,
                canManage: payload.caps.can_manage,
                scope: videosGrid, onRemove: decVideos,
            }));
            videoCount += 1;
        }
        hydrateIcons(videosGrid);
        setBadge(videosTab, videoCount);
        setTabVisible(videosTab, videoCount > 0);
        activate('videos');
    };

    // ── Unified media uploader (new manager UI): accepts images and videos in one dropzone, then
    // routes files to the existing picture/video endpoints internally. Only wired when the viewer can
    // manage (a read-only mount has no upload pane).
    if (includeUpload && uploadPane) {
        const mediaUploaderHost = uploadPane.querySelector<HTMLElement>('[data-ft-uploader-media]');
        if (mediaUploaderHost) {
            mountUploader(mediaUploaderHost, {
                config,
                itemId: payload.item_id,
                kind: 'media',
                onUploadedPictures: handleUploadedPictures,
                onUploadedVideos: handleUploadedVideos,
            });
        } else {
            // Legacy split uploader support for other mounts that still render separate hosts.
            const uploaderHost = uploadPane.querySelector<HTMLElement>('[data-ft-uploader]');
            if (uploaderHost) {
                mountUploader(uploaderHost, {
                    config,
                    itemId: payload.item_id,
                    kind: 'image',
                    onUploadedPictures: handleUploadedPictures,
                });
            }

            const videoUploaderHost = uploadPane.querySelector<HTMLElement>('[data-ft-uploader-video]');
            if (videoUploaderHost) {
                mountUploader(videoUploaderHost, {
                    config,
                    itemId: payload.item_id,
                    kind: 'video',
                    onUploadedVideos: handleUploadedVideos,
                });
            }
        }
    }

    // ── Initial pane: first non-empty gallery, else Upload (or Pictures on a read-only mount) ──
    activate(pictureCount > 0 ? 'pictures' : videoCount > 0 ? 'videos' : (includeUpload ? 'upload' : 'pictures'));

    // Hydrate the tab + initial-card icon placeholders we just built (don't depend on the footer
    // sweep's timing relative to this mount).
    hydrateIcons(root);
}

export function mountItemMediaAll(): void {
    document
        .querySelectorAll<HTMLElement>('[data-ft-item-media]:not([data-ft-item-media-mounted="1"])')
        .forEach(mountItemMedia);
}
